import { Show, createEffect, createSignal, mergeProps, on, onCleanup, onMount, useContext } from 'solid-js';
import { AngleUpIcon } from '../../components/icons-library/angle-up';
import { ErrorCatcher } from '../../tools/error-catcher';
import { Heading } from '../heading/heading';
import { ExpandableProps } from './expandable-container.d';
import { AppContext } from '../../app-context-provider/app-context-provider';
import theme from '../../style/theme';
import { Dynamic, isServer } from 'solid-js/web';
import clsx from 'clsx';

const defaultProps = {
    isDropdown: false,
};

const getHeadingRowStyle = (p: {
    backgroundColor?: string;
    customHeadingRowCss?: string;
    isOpen?: boolean;
    withBorder?: boolean;
    roundCorners?: boolean;
    disableClick?: boolean;
    withPadding?: boolean;
}) => `
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: border-bottom-left-radius 0.2s ease-in-out, border-bottom-right-radius 0.2s ease-in-out;
    ${!p.disableClick ? 'cursor: pointer;' : ''}
    ${p.backgroundColor ? `background-color: ${p.backgroundColor};` : ''}
    ${p.withPadding || p.withBorder ? 'padding-block: 1.5rem;' : 'padding-bottom: 0.5rem;'}
    border-top-left-radius: ${p.roundCorners ? theme.borderRadius.medium : 0};
    border-top-right-radius: ${p.roundCorners ? theme.borderRadius.medium : 0};
    border-bottom-left-radius: ${p.roundCorners && !p.isOpen ? theme.borderRadius.medium : 0};
    border-bottom-right-radius: ${p.roundCorners && !p.isOpen ? theme.borderRadius.medium : 0};
    ${p.customHeadingRowCss ? p.customHeadingRowCss : ''}
`;

const getContainerStyle = (p: { isDropdown: boolean; isOpen: boolean; withBorder?: boolean }) => {
    if (!p.isDropdown) return '';
    return `
        padding-inline: 1.5rem;
        position: absolute;
        right: 0;
        left: 0;
        border-radius: ${theme.borderRadius.small};
        ${p.withBorder ? `border: 1px solid ${theme.palette.darkGray};` : ''}
        z-index: 2;
        background-color: white;
        transition: box-shadow 0.2s ease-in-out, padding-bottom 0.2s ease-in-out;
        ${p.isOpen ? 'box-shadow: 0px 0.21rem 0.62rem rgba(0,0,0,0.19);' : ''}
    `;
};

// const getWrapperStyle = (p: { isDropdown: boolean; shrinkOnTablet?: boolean }) => `
//     width: 100%;
//     position: relative;
//     top: 0;
//     ${p.isDropdown ? 'height: 7.3rem;' : ''}
//     ${p.isDropdown && p.shrinkOnTablet ? `
//         ${theme.containerQueries.tabletMaxOriginal} {
//             height: 4rem;
//         }
//     ` : ''}
// `;

const getChildrenStyle = (p: { withDividerLine?: boolean; isOpen?: boolean }) => `
    position: relative;
    ${p.isOpen ? 'display: grid;' : 'display: none;'}
    ${p.withDividerLine ? `
        &::after {
            position: absolute;
            content: '';
            display: block;
            clear: both;
            left: 3rem;
            right: 3rem;
            height: 2px;
            background-color: ${theme.palette.darkGray};
            top: -1.5rem;
            ${theme.containerQueries.mobileMax} {
                height: 1px;
                top: -1rem;
                left: 1.5rem;
                right: 1.5rem;
            }
        }
    ` : ''}
`;

export const ExpandableContainer = (componentProps: ExpandableProps) => {
    const props = mergeProps(defaultProps, componentProps);

    const { viewport, localize } = useContext(AppContext);

    const headerElementId = Math.random().toString();

    let expandableRef: HTMLDivElement | undefined;
    let escapeListenerAdded = false;
    let outsideClickHandlerAdded = false;

    const [ open, setOpen ] = createSignal(false);
    const [ displayToGrid, setDisplayToGrid ] = createSignal(false);

    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;

    const openContainer = () => setOpen(true);
    const closeContainer = () => setOpen(false);

    onMount(() => {
        if (props.initialOpen) {
            triggerOpeningAnimation();
        }

        const header = document?.getElementById(headerElementId);
        if (header) {
            header.addEventListener('keydown', handleHeaderKeyPress);
        }
    });

    createEffect(
        on(
            [
                () => props.forceTimestamp,
                () => props.forceOpen,
                () => props.forceClose
            ],
            () => {
                if (props.forceOpen && !props.forceClose) openContainer();
                if (props.forceClose && !props.forceOpen) closeContainer();
            },
            { defer: true }
        ),
    );

    createEffect(() => {
        if (open()) {
            if (!escapeListenerAdded) {
                window.addEventListener('keydown', handleEscapeKey);
                escapeListenerAdded = true;
            }

            if (!outsideClickHandlerAdded) {
                if (!props.disableOutsideClickToClose && (isMobile() || props.enableOutsideClickToCloseOnDesktop)) {
                    window.addEventListener('click', handleClickOutside);
                    outsideClickHandlerAdded = true;
                }
            }
        } else if (!open()) {
            if (escapeListenerAdded) {
                window.removeEventListener('keydown', handleEscapeKey);
                escapeListenerAdded = false;
            }

            if (outsideClickHandlerAdded) {
                window.removeEventListener('click', handleClickOutside);
                outsideClickHandlerAdded = false;
            }
        }
    });

    onCleanup(() => {
        if (isServer) return;

        if (escapeListenerAdded) {
            window.removeEventListener('keydown', handleEscapeKey);
            escapeListenerAdded = false;
        }

        if (outsideClickHandlerAdded) {
            window.removeEventListener('click', handleClickOutside);
            outsideClickHandlerAdded = false;
        }

        const container = document?.getElementById(headerElementId);
        if (container) {
            container.removeEventListener('keydown', handleHeaderKeyPress);
        }
    });

    const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && open()) {
            if (expandableRef && expandableRef.contains(document.activeElement)) {
                triggerClosingAnimation();
            }
        }
    };

    const handleClickOutside = (event: Event) => {
        if (open() && !expandableRef?.contains(event.target as Node)) {
            triggerClosingAnimation();
        }
    };

    const handleHeaderKeyPress = (e: any) => {
        if (e.type === 'keydown' && e.keyCode === 32) {
            e.preventDefault();
            open()
                ? triggerClosingAnimation()
                : triggerOpeningAnimation();
        }
    };

    const triggerOpeningAnimation = () => {
        setDisplayToGrid(true);
        setTimeout(() => openContainer(), 0);
    };

    const triggerClosingAnimation = () => {
        closeContainer();
        setTimeout(() => setDisplayToGrid(false), 200);
    };

    const toggleOpenCloseState = () => {
        if (props.disableClick) {
            return;
        }

        open()
            ? triggerClosingAnimation()
            : triggerOpeningAnimation();
    };

    const headingRowTag = props.disableClick ? 'div' : 'button';

    return (
        <ErrorCatcher componentName="Expandable container">
            <div
                ref={expandableRef}
                class={clsx(
                    'w-full relative top-0',
                    props.isDropdown && 'h-[7.3rem]',
                    props.isDropdown && props.shrinkOnTablet && 'expandable-shrink-tablet',
                    props.isDropdown && 'hover:[&>div]:shadow-[0px_0.41rem_0.62rem_rgba(0,0,0,0.1)]',
                )}
            >
                <div style={getContainerStyle({ isOpen: open(), isDropdown: props.isDropdown, withBorder: props.withBorder })}>
                    <Dynamic
                        component={headingRowTag}
                        ref={props.ref}
                        id={headerElementId}
                        onClick={() => toggleOpenCloseState()}
                        aria-label={localize('expandable-container', 'Expandable container')}
                        class={props.customHeadingRowClass || ''}
                        style={getHeadingRowStyle({
                            backgroundColor: props.backgroundColor,
                            customHeadingRowCss: props.customHeadingRowCss,
                            isOpen: open(),
                            withBorder: props.withBorder,
                            roundCorners: props.roundCorners,
                            disableClick: props.disableClick,
                            withPadding: props.withPadding,
                        })}
                    >
                        <Heading {...props.headingProps} />
                        <Show when={!props.disableClick}>
                            <div class="inline-block w-[1.5rem]" />

                            <div class="relative">
                                <span
                                    class={clsx('block transform transition-transform duration-300 origin-center', {
                                        'rotate-180': !open(),
                                    })}
                                >
                                    <AngleUpIcon
                                        fill={props.chevronColor}
                                        width={props.chevronSize || 1.43}
                                        height={props.chevronSize || 1.43}
                                        ariaLabel={open() ? localize('close-submenu', 'Close Submenu') : localize('open-submenu', 'Open Submenu')}
                                    />
                                </span>
                            </div>
                        </Show>
                    </Dynamic>
                    <div
                        style={getChildrenStyle({ withDividerLine: props.withDividerLine, isOpen: open() })}
                        class={clsx('expandable-header', {
                            'is-open': open(),
                            'as-grid': displayToGrid(),
                        })}
                    >
                        <div class="expandable-body">
                            {props.children}
                        </div>
                    </div>
                </div>
            </div>
        </ErrorCatcher>
    );
};
